<script>
import { updatePlan } from '@/api/billing.js';

export default {
    data() {
        return {
            formData: {
                name: '',
                description: '',
                features: {},
                is_active: true,
                stripe_price_id: '',
                plan_type: 'personal',
            },
            newFeature: '',
            newFeatureKey: '',
            errors: {
                name: '',
                description: '',
                feature_key: '',
                feature_value: '',
                stripe_price_id: '',
                plan_type: '',
            },
            touched: {
                name: false,
                description: false,
            },
        };
    },
    computed: {
        isShow() {
            return this.$store.getters.IS_SHOW_PLAN_EDIT;
        },
        plan() {
            return this.$store.getters.PLAN_EDIT_DATA;
        },
        isFormValid() {
            return (
                !this.errors.name &&
                !this.errors.description &&
                !this.errors.stripe_price_id &&
                !this.errors.plan_type &&
                this.formData.name &&
                this.formData.description &&
                this.formData.plan_type
            );
        },
        nameCharCount() {
            return this.formData.name.length;
        },
        descriptionCharCount() {
            return this.formData.description.length;
        },
    },
    watch: {
        plan: {
            handler(newPlan) {
                if (newPlan) {
                    this.formData = {
                        name: newPlan.name,
                        description: newPlan.description,
                        features: { ...(newPlan.features || {}) },
                        is_active: newPlan.is_active,
                        stripe_price_id:
                            newPlan.provider_price_ids?.stripe || '',
                        plan_type: newPlan.plan_type || 'personal',
                    };
                    // Reset validation state when plan changes
                    this.errors = {
                        name: '',
                        description: '',
                        feature_key: '',
                        feature_value: '',
                        stripe_price_id: '',
                        plan_type: '',
                    };
                    this.touched = {
                        name: false,
                        description: false,
                    };
                }
            },
            immediate: true,
        },
    },
    methods: {
        validateName() {
            const name = this.formData.name.trim();

            if (!name) {
                this.errors.name = 'Plan name is required';
                return false;
            }
            if (name.length < 3) {
                this.errors.name = 'Plan name must be at least 3 characters';
                return false;
            }
            if (name.length > 100) {
                this.errors.name = 'Plan name cannot exceed 100 characters';
                return false;
            }
            if (!/^[a-zA-Z0-9\s\-_()]+$/.test(name)) {
                this.errors.name =
                    'Plan name can only contain letters, numbers, spaces, hyphens, underscores, and parentheses';
                return false;
            }

            this.errors.name = '';
            return true;
        },

        validateDescription() {
            const desc = this.formData.description.trim();

            if (!desc) {
                this.errors.description = 'Description is required';
                return false;
            }
            if (desc.length < 10) {
                this.errors.description =
                    'Description must be at least 10 characters';
                return false;
            }
            if (desc.length > 500) {
                this.errors.description =
                    'Description cannot exceed 500 characters';
                return false;
            }

            this.errors.description = '';
            return true;
        },

        validateFeatureKey(key) {
            const trimmedKey = key.trim();

            if (!trimmedKey) {
                this.errors.feature_key = 'Feature key is required';
                return false;
            }
            if (trimmedKey.length < 2) {
                this.errors.feature_key =
                    'Feature key must be at least 2 characters';
                return false;
            }
            if (trimmedKey.length > 50) {
                this.errors.feature_key =
                    'Feature key cannot exceed 50 characters';
                return false;
            }
            if (!/^[a-z0-9_]+$/.test(trimmedKey)) {
                this.errors.feature_key =
                    'Feature key must be in snake_case format (lowercase letters, numbers, and underscores only)';
                return false;
            }
            if (
                Object.prototype.hasOwnProperty.call(
                    this.formData.features,
                    trimmedKey
                )
            ) {
                this.errors.feature_key = 'Feature key already exists';
                return false;
            }

            this.errors.feature_key = '';
            return true;
        },

        validateFeatureValue(value) {
            const trimmedValue = value.trim();

            if (!trimmedValue) {
                this.errors.feature_value = 'Feature value is required';
                return false;
            }
            if (trimmedValue.length < 1) {
                this.errors.feature_value =
                    'Feature value must be at least 1 character';
                return false;
            }
            if (trimmedValue.length > 200) {
                this.errors.feature_value =
                    'Feature value cannot exceed 200 characters';
                return false;
            }

            this.errors.feature_value = '';
            return true;
        },

        validateStripePriceId() {
            const stripePriceId = this.formData.stripe_price_id.trim();

            // Empty is valid (optional field)
            if (!stripePriceId) {
                this.errors.stripe_price_id = '';
                return true;
            }

            if (stripePriceId.length > 100) {
                this.errors.stripe_price_id =
                    'Stripe Price ID cannot exceed 100 characters';
                return false;
            }

            this.errors.stripe_price_id = '';
            return true;
        },

        validatePlanType() {
            const planType = this.formData.plan_type;

            if (!planType) {
                this.errors.plan_type = 'Plan type is required';
                return false;
            }

            if (!['personal', 'corporate'].includes(planType)) {
                this.errors.plan_type =
                    'Plan type must be either "personal" or "corporate"';
                return false;
            }

            this.errors.plan_type = '';
            return true;
        },

        validateForm() {
            const isNameValid = this.validateName();
            const isDescValid = this.validateDescription();
            const isStripePriceIdValid = this.validateStripePriceId();
            const isPlanTypeValid = this.validatePlanType();

            return (
                isNameValid &&
                isDescValid &&
                isStripePriceIdValid &&
                isPlanTypeValid
            );
        },

        markFieldTouched(field) {
            this.touched[field] = true;
        },

        close() {
            this.$store.commit('IS_SHOW_PLAN_EDIT', false);
        },
        addFeature() {
            // Validate feature key and value
            if (!this.validateFeatureKey(this.newFeatureKey)) {
                return;
            }
            if (!this.validateFeatureValue(this.newFeature)) {
                return;
            }

            this.formData.features[this.newFeatureKey.trim()] =
                this.newFeature.trim();
            this.newFeature = '';
            this.newFeatureKey = '';
            this.errors.feature_key = '';
            this.errors.feature_value = '';
        },
        removeFeature(key) {
            delete this.formData.features[key];
        },
        async save() {
            // Mark all fields as touched
            Object.keys(this.touched).forEach((key) => {
                this.touched[key] = true;
            });

            // Validate entire form
            if (!this.validateForm()) {
                this.$store.commit('POPUP_ADD', {
                    title: 'VALIDATION ERROR',
                    description: [
                        'Please fix all validation errors before saving',
                    ],
                    color: '#ff9800',
                });
                return;
            }

            const success = await updatePlan(this.plan.id, this.formData);

            // Early return on failure
            if (!success) {
                return;
            }

            this.$store.commit('POPUP_ADD', {
                title: 'PLAN UPDATED',
                description: ['Plan has been successfully updated'],
                color: '#00d25b',
            });
            this.close();
            await this.$store.dispatch('loadBillingPlans');
        },
    },
};
</script>

<template>
    <div v-if="isShow && plan" class="popup_background">
        <div class="left_additional_container">
            <div class="left_sub_panel_header">
                <label>Edit Subscription Plan</label>
                <button class="button_blue_100" @click="close">Close</button>
                <button
                    class="button_blue_100"
                    @click="save"
                    :disabled="!isFormValid"
                >
                    Save
                </button>
            </div>
            <hr class="hr_light_98" />

            <div class="left_sub_panel_data">
                <label class="mb_5"
                    >Plan Name:
                    <span
                        class="char_counter"
                        :class="{ warning: nameCharCount > 90 }"
                    >
                        {{ nameCharCount }}/100
                    </span>
                </label>
                <input
                    v-model="formData.name"
                    type="text"
                    required
                    maxlength="100"
                    @blur="
                        markFieldTouched('name');
                        validateName();
                    "
                    @input="validateName()"
                    :class="{ error: touched.name && errors.name }"
                />
                <span v-if="touched.name && errors.name" class="error_message">
                    {{ errors.name }}
                </span>

                <label class="mb_5 mt_10"
                    >Description:
                    <span
                        class="char_counter"
                        :class="{ warning: descriptionCharCount > 450 }"
                    >
                        {{ descriptionCharCount }}/500
                    </span>
                </label>
                <textarea
                    v-model="formData.description"
                    rows="3"
                    required
                    maxlength="500"
                    @blur="
                        markFieldTouched('description');
                        validateDescription();
                    "
                    @input="validateDescription()"
                    :class="{
                        error: touched.description && errors.description,
                    }"
                ></textarea>
                <span
                    v-if="touched.description && errors.description"
                    class="error_message"
                >
                    {{ errors.description }}
                </span>

                <div class="readonly_section mt_10">
                    <label class="mb_5">Price (non-editable):</label>
                    <p>
                        {{ plan.price_currency }}
                        {{ (plan.price_amount / 100).toFixed(2) }} /
                        {{ plan.billing_period }}
                    </p>

                    <label class="mb_5 mt_10">Limits (non-editable):</label>
                    <p>Trial: {{ plan.trial_days }} days</p>
                    <p>Max Downloads: {{ plan.max_downloads }}</p>

                    <label class="mb_5 mt_10">Stripe Integration:</label>
                    <p>
                        Price ID:
                        {{
                            plan.provider_price_ids?.stripe || 'Not configured'
                        }}
                    </p>

                    <small class="warning_text">
                        ⚠ Price and limits cannot be changed after plan creation
                    </small>
                </div>

                <label class="mb_5 mt_10">Stripe Price ID (optional):</label>
                <small class="help_text mb_5">
                    Leave empty to remove Stripe integration. Format:
                    price_xxxxxxxxxxxxxxxxxxxxx
                </small>
                <input
                    v-model="formData.stripe_price_id"
                    type="text"
                    placeholder="price_1234567890abcdefghijklmn"
                    maxlength="100"
                    @blur="validateStripePriceId()"
                    @input="validateStripePriceId()"
                    :class="{ error: errors.stripe_price_id }"
                    disabled
                />
                <small class="warning_text"
                    >Cannot be changed after creation</small
                >
                <span v-if="errors.stripe_price_id" class="error_message">
                    {{ errors.stripe_price_id }}
                </span>

                <label class="mb_5 mt_10">Plan Type:</label>
                <small class="warning_text mb_5"
                    >Cannot be changed after creation</small
                >
                <div class="radio_group">
                    <label class="radio_label">
                        <input
                            type="radio"
                            v-model="formData.plan_type"
                            value="personal"
                            @change="validatePlanType()"
                            disabled
                        />
                        <span class="radio_text">Personal</span>
                        <span class="radio_description"
                            >For individual users</span
                        >
                    </label>
                    <label class="radio_label">
                        <input
                            type="radio"
                            v-model="formData.plan_type"
                            value="corporate"
                            @change="validatePlanType()"
                            disabled
                        />
                        <span class="radio_text">Corporate</span>
                        <span class="radio_description"
                            >For business accounts</span
                        >
                    </label>
                </div>
                <span v-if="errors.plan_type" class="error_message">
                    {{ errors.plan_type }}
                </span>

                <label class="mb_5 mt_10">Features:</label>
                <small class="help_text mb_5"
                    >Example key: unlimited_downloads</small
                >
                <div
                    v-for="(value, key) in formData.features"
                    :key="key"
                    class="feature_item"
                >
                    <div class="feature_content">
                        <strong>{{ key }}:</strong> {{ value }}
                    </div>
                    <button class="button_red" @click="removeFeature(key)">
                        Remove
                    </button>
                </div>
                <div class="feature_add">
                    <div class="feature_key_wrapper">
                        <input
                            v-model="newFeatureKey"
                            type="text"
                            placeholder="Feature key (e.g., downloads)"
                            maxlength="50"
                            class="feature_key_input"
                            :class="{ error: errors.feature_key }"
                        />
                        <span v-if="errors.feature_key" class="error_message">
                            {{ errors.feature_key }}
                        </span>
                    </div>
                    <div class="feature_value_wrapper">
                        <input
                            v-model="newFeature"
                            type="text"
                            placeholder="Feature value (e.g., Unlimited downloads)"
                            maxlength="200"
                            @keyup.enter="addFeature"
                            class="feature_value_input"
                            :class="{ error: errors.feature_value }"
                        />
                        <span v-if="errors.feature_value" class="error_message">
                            {{ errors.feature_value }}
                        </span>
                    </div>
                    <button
                        class="margin_left button_blue_100"
                        @click="addFeature"
                    >
                        Add
                    </button>
                </div>

                <label class="mb_5 mt_10">
                    <input type="checkbox" v-model="formData.is_active" />
                    Active
                </label>

                <div
                    v-if="plan.active_subscriptions > 0"
                    class="info_box mt_10"
                >
                    ⚠ This plan has {{ plan.active_subscriptions }} active
                    subscription(s). Deactivating will prevent new signups but
                    preserve existing subscriptions.
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
input.error,
textarea.error,
select.error {
    border: 2px solid #f44336;
    background: rgba(244, 67, 54, 0.05);
}

.error_message {
    display: block;
    color: #f44336;
    font-size: 12px;
    margin-top: 4px;
    margin-bottom: 8px;
    font-weight: 500;
}

.help_text {
    display: block;
    margin-top: 4px;
    color: var(--text-color-secondary);
    font-size: 12px;
}

.char_counter {
    float: right;
    font-size: 11px;
    color: var(--text-color-secondary);
    font-weight: normal;
}

.char_counter.warning {
    color: #ff9800;
    font-weight: 600;
}

button:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.readonly_section {
    background: var(--background-hover);
    padding: 15px;
    border-radius: 8px;
}

.readonly_section p {
    margin: 5px 0;
    color: var(--text-color-secondary);
}

.warning_text {
    color: #ff9800;
    display: block;
    margin-top: 10px;
}

.info_box {
    background: rgba(255, 152, 0, 0.1);
    border-left: 3px solid #ff9800;
    padding: 12px;
    border-radius: 4px;
    color: var(--text-color);
}

.feature_item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px;
    margin-bottom: 5px;
    background: var(--background-hover);
    border-radius: 4px;
}

.feature_content {
    flex: 1;
}

.feature_add {
    display: flex;
    gap: 8px;
    margin-top: 10px;
    align-items: flex-start;
}

.feature_key_wrapper {
    display: flex;
    flex-direction: column;
    min-width: 200px;
}

.feature_value_wrapper {
    display: flex;
    flex-direction: column;
    flex: 1;
}

.feature_key_input {
    width: 100%;
}

.feature_value_input {
    margin-left: 10px;
    width: 100%;
}

.margin_left {
    margin-left: 20px;
}

.radio_group {
    display: flex;
    gap: 15px;
    margin-bottom: 10px;
}

.radio_label {
    display: flex;
    align-items: center;
    cursor: pointer;
    padding: 10px 15px;
    border: 2px solid var(--border-color);
    border-radius: 6px;
    transition: all 0.2s ease;
    flex: 1;
}

.radio_label:hover {
    border-color: var(--primary-color);
    background: var(--background-hover);
}

.radio_label input[type='radio'] {
    margin-right: 10px;
    cursor: pointer;
}

.radio_label input[type='radio']:checked + .radio_text {
    color: var(--primary-color);
    font-weight: 600;
}

.radio_text {
    font-weight: 500;
    margin-right: 8px;
}

.radio_description {
    font-size: 12px;
    color: var(--text-color-secondary);
    margin-left: auto;
}
</style>
